import React, { useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import moment from 'moment';
import {
    Dialog, DialogContent, makeStyles,
    Button,
} from '@material-ui/core';
import { modules } from 'utils/enum/modules';
import { ServiceSubModules } from 'utils/enum/ServiceInvoiceEnum';
import { Col, Form } from 'react-bootstrap';
import { EventStatus } from 'utils/enum/ActivitiesEnum';
import { useMutation } from '@apollo/client';
import DateUtils from 'lib/DateUtils';
import TimePicker from 'rc-time-picker';
import 'rc-time-picker/assets/index.css';
import DatePicker from 'react-datepicker';
import StringUtils from 'lib/StringUtils';
import ModalUtils from 'utils/ModalUtils';
import ButtonStyles from 'styles/theme/Button';
import DialogAppBar from 'components/widgets/modal/DialogAppBar';
import Select from 'components/widgets/Select';
import CalendarContainer from 'components/widgets/form/CalendarContainer';
import DialogActions from '@material-ui/core/DialogActions';
import CustomerForServiceList from 'components/modules/service/serviceInvoice/create/CustomerForServiceList';
import AddNewServiceVehicle from 'components/modules/service/serviceInvoice/create/AddNewServiceVehicle';
import VehiclesByCustomerList from 'components/modules/service/serviceInvoice/create/VehiclesByCustomerList';
import CreateCustomer from 'components/modules/deals/create/dealTab/CreateCustomer';
import ServiceMutation from 'services/graphQL/mutate/service/ServiceMutation';

const buttonStyles = makeStyles((theme) => ButtonStyles.getStyle(theme));
const useStyles = makeStyles((theme) => ({
    AppBar: {
        color: theme.palette.text.white,
        backgroundColor: theme.palette.background.sanMarino,
        '& h4': {
            color: theme.palette.text.white,
        },
    },
    formRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        '& > button': {
            marginRight: '5px',
            height: '33px',
            marginTop: '8px',
        },
        '& > div > *': {
            fontSize: '13px',
        },
    },
    dateRow: {
        display: 'flex',
        justifyContent: 'space-between',
        '& > div:nth-child(1)': {
            flexShrink: 0,
            marginRight: '10px',
            width: '200px',
        },
        '& > span:nth-child(2)': {
            flexGrow: 1,
            marginRight: '10px',
        },
        '& > div:nth-child(3)': {
            flexGrow: 1,
        },
    },
    textArea: {
        marginRight: theme.spacing(2),
        resize: 'none',
        height: '120px !important',
    },
    actions: {
        justifyContent: 'space-between',
        marginLeft: '30px',
        marginRight: '30px',
        '& > div:nth-child(2) > button:nth-child(1)': {
            marginRight: '5px',
        },
    },
    dialogContent: {
        height: 300,
        padding: theme.spacing(1, 1.8),
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
    },
}));

const ServiceAppointmentForm = ({
    onClose,
    advisors,
    selectedServiceRecord,
    refetchServiceAppointments,
}) => {
    const classes = { ...useStyles(), ...buttonStyles() };
    const [state, setState] = useState({
        ...(selectedServiceRecord ? {
            selectedActivityId: selectedServiceRecord.activityId,
            selectedCustomer: selectedServiceRecord.customer,
            selectedVehicle: selectedServiceRecord.vehicle,
            selectedStatus: selectedServiceRecord.status,
            selectedDate: DateUtils.toLocal(selectedServiceRecord.startsAt).toDate(),
            selectedStartTime: DateUtils.toLocal(selectedServiceRecord.startsAt),
            selectedEndTime: DateUtils.toLocal(selectedServiceRecord.endsAt),
            selectedReason: selectedServiceRecord.reason,
            selectedAdvisor: selectedServiceRecord.advisor,
        } : {
            selectedActivityId: null,
            selectedCustomer: null,
            selectedVehicle: null,
            selectedStatus: EventStatus.SCHEDULED,
            selectedDate: moment().toDate(),
            selectedStartTime: null,
            selectedEndTime: null,
            selectedReason: null,
            selectedAdvisor: null,
        }),
        openCustomerDialog: false,
        openVehicleDialog: false,
        openNewCustomerDialog: false,
        openNewVehicleDialog: false,
    });

    const {
        selectedActivityId,
        selectedCustomer,
        selectedVehicle,
        selectedStatus,
        selectedDate,
        selectedStartTime,
        selectedEndTime,
        selectedReason,
        selectedAdvisor,
        openCustomerDialog,
        openVehicleDialog,
        openNewCustomerDialog,
        openNewVehicleDialog,
    } = state;

    const [saveAppointment, { loading: savingAppointment }] = useMutation(ServiceMutation.SAVE_SERVICE_APPOINTMENT, {
        onCompleted: () => {
            ModalUtils.successMessage(null, 'Appointment saved successfully');

            onClose();
            refetchServiceAppointments();
        },
        onError: (error) => {
            ModalUtils.errorMessage(null, error);
        },
    });

    const onChange = (name, value) => {
        if (name === 'selectedDate') {
            setState((prevState) => {
                const currentEndTime = prevState.selectedEndTime != null
                    ? moment(value).set({ minute: prevState.selectedEndTime.get('minute'), hour: prevState.selectedEndTime.get('hour') })
                    : null;
                const currentStartTime = prevState.selectedStartTime != null
                    ? moment(value).set({ minute: prevState.selectedStartTime.get('minute'), hour: prevState.selectedStartTime.get('hour') })
                    : null;

                return {
                    ...prevState,
                    [name]: value,
                    selectedStartTime: currentStartTime,
                    selectedEndTime: currentEndTime,
                };
            });
        }

        if (name === 'selectedStartTime') {
            const currentTime = moment(value);
            const hoursMinutes = { minute: currentTime.get('minute'), hour: currentTime.get('hour') };
            const generatedEndTime = DateUtils.add(currentTime, 1, 'hours');

            setState((prevState) => ({
                ...prevState,
                selectedStartTime: moment(prevState.selectedDate).set(hoursMinutes),
                selectedEndTime: moment(prevState.selectedDate).set({ minute: generatedEndTime.get('minute'), hour: generatedEndTime.get('hour') }),
            }));
        }

        if (name === 'selectedEndTime') {
            setState((prevState) => {
                if (prevState.selectedEndTime == null) {
                    const time = moment(value, 'HH:mm');
                    const currentEndTime = moment(prevState.selectedDate).set({
                        hour: time.get('hour'),
                        minute: time.get('minute'),
                    });

                    return {
                        ...prevState,
                        selectedEndTime: currentEndTime,
                    };
                }

                return {
                    ...prevState,
                    selectedEndTime: value,
                };
            });
        }

        setState((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const onSelectCustomer = (val) => {
        const { customer, vehicle } = val;

        setState((prevState) => ({
            ...prevState,
            openCustomerDialog: false,
            ...(customer ? {
                selectedCustomer: customer,
            } : {}),
            ...(vehicle ? {
                selectedVehicle: vehicle,
            } : {}),
        }));
    };

    const onSelectVehicle = (val) => {
        setState((prevState) => ({
            ...prevState,
            openVehicleDialog: false,
            selectedVehicle: val,
        }));
    };

    const clearField = (field) => {
        setState((prevState) => ({
            ...prevState,
            [field]: null,
            ...(field === 'selectedCustomer' ? {
                selectedVehicle: null,
            } : {}),
        }));
    };

    const onSetCustomer = (val) => {
        setState((prevState) => ({
            ...prevState,
            selectedVehicle: null,
            selectedCustomer: val,
        }));
    };

    const onSaveAppointment = (isDelete = false) => {
        const { customerCode } = selectedCustomer;
        const {
            stockNumber,
            engine,
            make,
            model,
            trim,
            vin,
            year,
        } = selectedVehicle;

        const currentStart = DateUtils.formatUTC(selectedDate);
        const currentStartTime = selectedStartTime ? moment(currentStart).set({
            hour: selectedStartTime.hour(), minute: selectedStartTime.minute(), second: 0, millisecond: 0,
        }) : currentStart;
        const currentEndTime = selectedEndTime ? moment(currentStart).set({
            hour: selectedEndTime.hour(), minute: selectedEndTime.minute(), second: 0, millisecond: 0,
        }) : currentStart;

        const lot = advisors.find((a) => a.userId === selectedAdvisor)?.defaultLot;
        if (!lot) {
            ModalUtils.errorMessage(null, 'Selected advisor has no default lot');
            return;
        }

        saveAppointment({
            variables: {
                input: {
                    activityId: selectedActivityId,
                    customerCode,
                    vehicle: {
                        stock: stockNumber,
                        engine,
                        make,
                        model,
                        trim,
                        vin,
                        ...(year ? { year: String(year) } : {}),
                    },
                    startsAt: DateUtils.formatUTC(currentStartTime),
                    endsAt: DateUtils.formatUTC(currentEndTime),
                    status: selectedStatus,
                    reason: selectedReason,
                    advisor: selectedAdvisor,
                    lot,
                    ...(isDelete ? { active: false } : {}),
                },
            },
        });
    };

    const openNewRO = () => {
        const lot = advisors.find((a) => a.userId === selectedAdvisor)?.defaultLot;

        const {
            stockNumber,
            year,
            make,
            model,
            trim,
            vin,
            engine,
        } = selectedVehicle;
        const vehicleQuery = `vehicleStockNumber=${stockNumber ?? ''}&vehicleYear=${year ?? ''}&vehicleMake=${make ?? ''}&vehicleModel=${model ?? ''}&
vehicleTrim=${trim ?? ''}&vehicleVin=${vin ?? ''}&vehicleEngine=${engine ?? ''}`;

        const queryString = `lot=${lot}&customerCode=${selectedCustomer.customerCode}&customerEmail=${selectedCustomer.email ?? ''}&
customerCellphone=${selectedCustomer.cellPhone ?? ''}&customerName=${`${selectedCustomer.firstName} ${selectedCustomer.lastName}`}&
advisor=${selectedAdvisor}&reason=${selectedReason}&${vehicleQuery}`;
        window.open(`/${modules.SERVICE}/${ServiceSubModules.REPAIRS_ORDERS}/customer/create?${queryString}`, '_blank');
    };

    const eventStatus = Object
        .keys(EventStatus)
        .map((c) => ({ value: EventStatus[c], label: EventStatus[c] === EventStatus.SHOW ? 'Showed' : EventStatus[c] }));
    const isSaveButtonDisabled = !selectedCustomer
        || !selectedVehicle
        || !selectedStartTime
        || !selectedEndTime
        || !selectedAdvisor
        || StringUtils.isEmpty(selectedReason)
        || savingAppointment;
    return (
        <>
            <Dialog
                open
                fullWidth
                maxWidth="sm"
                disableBackdropClick
                disableEscapeKeyDown
                scroll="paper"
                onMouseDown={(e) => e.stopPropagation()}
            >
                <DialogAppBar
                    appBarClassName={classes.AppBar}
                    title="Appointment"
                    onClose={() => onClose()}
                    toolbarSize="md"
                />
                <DialogContent>
                    <div className={classes.formRow}>
                        <Form.Group as={Col}>
                            <Form.Label className={classes.dialogLabels}>Customer</Form.Label>
                            <Form.Control
                                readOnly
                                className={clsx(classes.input, !selectedCustomer ? 'invalid-field' : '')}
                                type="text"
                                name="selectedCustomer"
                                defaultValue={selectedCustomer ? `${selectedCustomer.firstName} ${selectedCustomer.lastName}`.toUpperCase() : ''}
                            />
                        </Form.Group>
                        <Button
                            size="small"
                            className={classes.containedSecondaryInfo}
                            onClick={() => setState((prevState) => ({ ...prevState, openCustomerDialog: true }))}
                        >
                            Select
                        </Button>
                        <Button
                            size="small"
                            className={classes.containedError}
                            onClick={() => clearField('selectedCustomer')}
                        >
                            Clear
                        </Button>
                    </div>
                    <div className={classes.formRow}>
                        <Form.Group as={Col}>
                            <Form.Label className={classes.dialogLabels}>Vehicle</Form.Label>
                            <Form.Control
                                readOnly
                                className={clsx(classes.input, !selectedVehicle ? 'invalid-field' : '')}
                                type="text"
                                name="selectedVehicle"
                                defaultValue={selectedVehicle
                                    ? `${selectedVehicle.stockNumber
                                        ? `#${selectedVehicle.stockNumber}`
                                        : ''} ${selectedVehicle.year} ${selectedVehicle.make} ${selectedVehicle.model} ${selectedVehicle.trim ?? ''}`
                                        .toUpperCase()
                                    : ''}
                            />
                        </Form.Group>
                        <Button
                            size="small"
                            disabled={!selectedCustomer}
                            className={classes.containedSecondaryInfo}
                            onClick={() => setState((prevState) => ({ ...prevState, openVehicleDialog: true }))}
                        >
                            Select
                        </Button>
                        <Button
                            size="small"
                            className={classes.containedError}
                            onClick={() => clearField('selectedVehicle')}
                        >
                            Clear
                        </Button>
                    </div>
                    <div className={classes.formRow}>
                        <Form.Group as={Col}>
                            <Form.Label className={classes.dialogLabels}>Status</Form.Label>
                            <Select
                                name="selectedStatus"
                                maxMenuHeight={100}
                                options={eventStatus}
                                value={selectedStatus}
                                onChange={(name, value) => onChange(name, value)}
                            />
                        </Form.Group>
                    </div>
                    <div className={classes.formRow}>
                        <Form.Group as={Col}>
                            <Form.Label className={classes.dialogLabels}>Date & Time</Form.Label>
                            <div className={classes.dateRow}>
                                <DatePicker
                                    className={clsx('form-control', { 'invalid-field': !selectedDate })}
                                    selected={selectedDate}
                                    popperContainer={CalendarContainer}
                                    onChange={(date) => onChange('selectedDate', date)}
                                />
                                <TimePicker
                                    use12Hours
                                    minuteStep={15}
                                    showSecond={false}
                                    value={selectedStartTime}
                                    placeholder="hh:mm"
                                    onChange={(e) => onChange('selectedStartTime', e)}
                                    className={clsx('am-time-picker form-control', { 'invalid-field': !selectedStartTime })}
                                />
                                <TimePicker
                                    use12Hours
                                    minuteStep={15}
                                    showSecond={false}
                                    value={selectedEndTime}
                                    placeholder="hh:mm"
                                    disabled={selectedStartTime == null}
                                    onChange={(e) => onChange('selectedEndTime', e)}
                                    className={clsx('am-time-picker form-control', { 'invalid-field': !selectedEndTime })}
                                />
                            </div>
                        </Form.Group>
                    </div>
                    <div className={classes.formRow}>
                        <Form.Group as={Col}>
                            <Form.Label className={classes.dialogLabels}>Reason</Form.Label>
                            <Form.Control
                                as="textarea"
                                maxLength={200}
                                value={selectedReason ?? ''}
                                className={clsx({ 'invalid-field': !selectedReason }, classes.textArea)}
                                onChange={(e) => onChange('selectedReason', e.target.value)}
                            />
                        </Form.Group>
                    </div>
                    <div className={classes.formRow}>
                        <Form.Group as={Col}>
                            <Form.Label className={classes.dialogLabels}>Advisor</Form.Label>
                            <Select
                                name="selectedAdvisor"
                                maxMenuHeight={200}
                                options={advisors.map((a) => ({ label: `${a.firstName} ${a.lastName} - ${a.defaultLot}`, value: a.userId }))}
                                value={selectedAdvisor}
                                onChange={(name, value) => onChange(name, value)}
                                className={clsx({ 'invalid-field': !selectedAdvisor })}
                            />
                        </Form.Group>
                    </div>
                </DialogContent>
                <DialogActions className={classes.actions}>
                    <div>
                        <Button
                            size="small"
                            disabled={isSaveButtonDisabled}
                            className={classes.containedSecondaryInfo}
                            onClick={openNewRO}
                        >
                            Open New RO
                        </Button>
                    </div>
                    <div>
                        <Button
                            size="small"
                            disabled={isSaveButtonDisabled}
                            className={classes.containedSecondaryInfo}
                            onClick={() => onSaveAppointment()}
                        >
                            Save
                        </Button>
                        <Button
                            size="small"
                            disabled={!selectedActivityId || savingAppointment}
                            className={classes.containedError}
                            onClick={() => onSaveAppointment(true)}
                        >
                            Delete
                        </Button>
                    </div>
                </DialogActions>
            </Dialog>
            {openCustomerDialog && (
                <CustomerForServiceList
                    inHouse={false}
                    onClose={() => setState((prevState) => ({ ...prevState, openCustomerDialog: false }))}
                    onSelectCustomer={(val) => onSelectCustomer(val)}
                    showAddNewCustomerButton
                    onClickNewCustomerButton={() => setState((prevState) => ({ ...prevState, openCustomerDialog: false, openNewCustomerDialog: true }))}
                    onClickNewVehicleButton={(val) => setState((prevState) => ({
                        ...prevState,
                        selectedCustomer: val,
                        openCustomerDialog: false,
                        openNewVehicleDialog: true,
                    }))}
                />
            )}
            {openVehicleDialog && selectedCustomer && (
                <VehiclesByCustomerList
                    code={selectedCustomer.customerCode}
                    inHouse={false}
                    onClose={() => setState((prevState) => ({ ...prevState, openVehicleDialog: false }))}
                    onSelectVehicle={(val) => onSelectVehicle(val)}
                    onClickNewVehicleButton={() => setState((prevState) => ({ ...prevState, openVehicleDialog: false, openNewVehicleDialog: true }))}
                />
            )}
            {openNewCustomerDialog && (
                <Dialog
                    open
                    fullWidth
                    maxWidth="md"
                >
                    <DialogAppBar
                        title="Create new customer"
                        onClose={() => setState((prevState) => ({ ...prevState, openNewVehicleDialog: false }))}
                    />
                    <DialogContent className={classes.dialogContent}>
                        <CreateCustomer
                            onlyRequireGeneralInfo
                            toggleCreateCustomer={() => setState((prevState) => ({ ...prevState, openNewCustomerDialog: false }))}
                            onSetCustomer={(val) => onSetCustomer(val)}
                        />
                    </DialogContent>
                </Dialog>
            )}
            {openNewVehicleDialog && (
                <AddNewServiceVehicle
                    noDecodingNeeded
                    onAddVehicle={(vehicleData) => setState((prevState) => ({ ...prevState, selectedVehicle: vehicleData, openNewVehicleDialog: false }))}
                    onPopupClose={() => setState((prevState) => ({ ...prevState, openNewVehicleDialog: false }))}
                />
            )}
        </>
    );
};

ServiceAppointmentForm.defaultProps = {
    selectedServiceRecord: null,
    refetchServiceAppointments: () => null,
};

ServiceAppointmentForm.propTypes = {
    onClose: PropTypes.func.isRequired,
    advisors: PropTypes.array.isRequired,
    selectedServiceRecord: PropTypes.object,
    refetchServiceAppointments: PropTypes.func,
};

export default ServiceAppointmentForm;
